import NavigationContent from '@modules/utilities/interfaces/NavigationContent';
import NavigationSectionItem from '@modules/utilities/interfaces/NavigationSectionItem';
import NavigationItem from '@modules/utilities/interfaces/NavigationItem';
import { TNavigationContentItem } from '@rbx/docsite-utilities';
import { getUrlData } from '@modules/localization/utils/localeUtils';

// Removes locale prefixes from the path
const normalizePath = (path: string | null | undefined): string => {
  if (!path) return '';
  const { subPath } = getUrlData(path);
  return subPath;
};

export const CLOUD_DOCS_HOME_PAGE = '/cloud';

export const isCloudHomePage = (path: string | null | undefined): boolean => {
  const normalizedPath = normalizePath(path);
  return normalizedPath === CLOUD_DOCS_HOME_PAGE;
};

const isIAV2FeaturesPage = (path: string | null | undefined): boolean => {
  const normalizedPath = normalizePath(path);
  return normalizedPath.startsWith('/cloud/reference/features/');
};

const isIAV2DomainPage = (path: string | null | undefined): boolean => {
  const normalizedPath = normalizePath(path);
  return normalizedPath.startsWith('/cloud/reference/domains/');
};

export const isIAV1FeaturePage = (path: string | null | undefined): boolean => {
  const normalizedPath = normalizePath(path);
  return normalizedPath.includes('/cloud/features/');
};

export const isIAV1CloudApiPage = (path: string | null | undefined): boolean => {
  const normalizedPath = normalizePath(path);
  return normalizedPath.includes('/cloud/api/');
};

const isIAV1CloudV1ApiPage = (path: string | null | undefined): boolean => {
  const normalizedPath = normalizePath(path);
  return normalizedPath.includes('/reference/cloud/');
};

// Checks for capitalization of the next character after the /cloud/reference/ string
// e.g. /cloud/reference/DataStores
// This avoids matching common pages like /cloud/reference/patterns
const isIAV1CloudV2ApiPage = (path: string | null | undefined): boolean => {
  const normalizedPath = normalizePath(path);
  if (!normalizedPath) return false;
  const searchString = '/cloud/reference/';
  const startIndex = normalizedPath.indexOf(searchString);
  if (startIndex < 0) return false;
  const nextChar = normalizedPath.charAt(startIndex + searchString.length);
  return nextChar.toUpperCase() === nextChar;
};

const isIAV1LegacyApiPage = (path: string | null | undefined): boolean => {
  const normalizedPath = normalizePath(path);
  return normalizedPath.includes('/cloud/legacy/');
};

const extractPathsFromSection = (
  sectionItem: NavigationItem | NavigationSectionItem | TNavigationContentItem,
): string[] => {
  const paths: string[] = [];

  // Add the current item's path if it exists
  if ('path' in sectionItem && sectionItem.path !== null && sectionItem.path !== undefined) {
    paths.push(sectionItem.path);
  }

  // Recursively extract paths from nested sections
  if ('section' in sectionItem && sectionItem.section) {
    sectionItem.section.forEach((nestedItem) => {
      paths.push(...extractPathsFromSection(nestedItem));
    });
  }

  return paths;
};

export const isIAV2FeaturesNav = (item: NavigationContent): boolean => {
  return item.navigation?.every((nav) => isIAV2FeaturesPage(nav.path)) ?? false;
};

export const isIAV2DomainNav = (item: NavigationContent): boolean => {
  return item.navigation?.every((nav) => isIAV2DomainPage(nav.path)) ?? false;
};

export const isIAV1FeaturesNav = (item: NavigationContent): boolean => {
  return (
    item.navigation?.every((nav) => {
      if ('path' in nav) {
        return isIAV1FeaturePage(nav.path);
      }
      return false;
    }) ?? false
  );
};

export const isIAV1CloudNav = (item: NavigationContent): boolean => {
  const allAreSections = item.navigation?.every((nav) => 'section' in nav) ?? false;
  if (!allAreSections) return false;

  return (
    item.navigation?.every((nav) => {
      if ('section' in nav && nav.section) {
        // Extract all paths from this nav item's sections
        const allPaths: string[] = [];
        nav.section.forEach((sectionItem) => {
          allPaths.push(...extractPathsFromSection(sectionItem));
        });

        // If there are no valid paths, this nav item doesn't match
        if (allPaths.length === 0) return false;

        // Check if all paths match at least one of the IAV1 cloud API patterns
        return allPaths.every((path) => {
          return (
            isIAV1CloudApiPage(path) || isIAV1CloudV1ApiPage(path) || isIAV1CloudV2ApiPage(path)
          );
        });
      }
      return false;
    }) ?? false
  );
};

export const isIAV1LegacyNav = (item: NavigationContent): boolean => {
  const allAreSections = item.navigation?.every((nav) => 'section' in nav) ?? false;
  if (!allAreSections) return false;

  return (
    item.navigation?.every((nav) => {
      if ('section' in nav && nav.section) {
        // Extract all paths from this nav item's sections
        const allPaths: string[] = [];
        nav.section.forEach((sectionItem) => {
          allPaths.push(...extractPathsFromSection(sectionItem));
        });

        // If there are no valid paths, this nav item doesn't match
        if (allPaths.length === 0) return false;

        // Check if all paths match the legacy API pattern
        return allPaths.every((path) => isIAV1LegacyApiPage(path));
      }
      return false;
    }) ?? false
  );
};
